import Parse from "parse";
import type { FestivalCalcStore } from "../pages/FestivalCalcPage/store";
import type { ScenarioTag, EventSegment } from "../pages/FestivalCalcPage/types";

// ─── Legacy JSON types (pure.html format) ─────────────────────────────────────

export interface LegacyFestivalData {
  festivalAdults?: number | string;
  festivalKids?: number | string;
  festAdults?: number | string;
  festKids?: number | string;
  zusagenPercent?: number | string;
  aufbau?: number | string;
  abbau?: number | string;
  infra?: LegacyInfraItem[];
  essen?: LegacyEssenItem[];
  getraenke?: LegacyGetraenkeItem[];
}

interface LegacyInfraItem {
  active?: boolean;
  item?: string;
  typ?: string;
  amount?: number | string;
}

interface LegacyEssenItem {
  active?: boolean;
  item?: string;
  price?: number | string;
  persons?: number | string;
}

interface LegacyGetraenkeItem {
  active?: boolean;
  item?: string;
  typ?: string;
  einheiten?: number | string;
  einheit?: string;
  preis?: number | string;
  proKopf?: number | string;
  isIngredient?: boolean;
  zutaten?: LegacyZutat[];
  _calcPreis?: number;
}

interface LegacyZutat {
  active?: boolean;
  item?: string;
  einheiten?: number | string;
  einheit?: string;
  gebindeGroesse?: number | string;
  gebindePreis?: number | string;
  preis?: number | string;
}

// ─── Analysis types ───────────────────────────────────────────────────────────

export interface SegmentToImport {
  name: string;
  attendeeCount: number;
}

export interface DuplicateItem {
  section: "infra" | "food" | "drink";
  name: string;
}

export interface ImportAnalysis {
  segmentsToCreate: SegmentToImport[];
  duplicates: DuplicateItem[];
}

export interface SegmentDecision {
  importedName: string;
  type: "new" | "existing";
  existingId?: string;
  updateCount?: boolean;
}

export interface ImportOptions {
  segmentDecisions: SegmentDecision[];
  itemsToOverwrite: Set<string>; // lowercase item names
}

// ─── Result ───────────────────────────────────────────────────────────────────

export interface ImportResult {
  infraCount: number;
  foodCount: number;
  drinkCount: number;
  recipeCount: number;
  segmentsCreated: string[];
  tagCreated: string | null;
  itemsSkipped: number;
  itemsOverwritten: number;
  warnings: string[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toNum(v: number | string | undefined, fallback = 0): number {
  const n = Number(v);
  return isNaN(n) ? fallback : n;
}

function toInt(v: number | string | undefined, fallback = 0): number {
  return Math.max(0, Math.round(toNum(v, fallback)));
}

function generateId(): string {
  return Math.random().toString(36).slice(2, 10);
}

// ─── Analysis ─────────────────────────────────────────────────────────────────

export function analyzeImport(
  data: LegacyFestivalData,
  store: FestivalCalcStore
): ImportAnalysis {
  const festivalAdults = toInt(data.festivalAdults);
  const festAdults = toInt(data.festAdults);

  const segmentsToCreate: SegmentToImport[] = [];
  if (festivalAdults > 0) {
    segmentsToCreate.push(
      { name: "Import: Tag 1", attendeeCount: festivalAdults },
      { name: "Import: Tag 2", attendeeCount: festivalAdults }
    );
  }
  if (festAdults > 0) {
    segmentsToCreate.push({ name: "Import: Setup/Abbau", attendeeCount: festAdults });
  }

  const existingInfraNames = new Set(store.infraItems.map((i) => i.name.toLowerCase()));
  const existingFoodNames = new Set(store.foodItems.map((i) => i.name.toLowerCase()));
  const existingDrinkNames = new Set(store.drinkItems.map((i) => i.name.toLowerCase()));

  const duplicates: DuplicateItem[] = [];
  for (const item of data.infra ?? []) {
    if (item.item && existingInfraNames.has(item.item.toLowerCase())) {
      duplicates.push({ section: "infra", name: item.item });
    }
  }
  for (const item of data.essen ?? []) {
    if (item.item && existingFoodNames.has(item.item.toLowerCase())) {
      duplicates.push({ section: "food", name: item.item });
    }
  }
  for (const item of (data.getraenke ?? []).filter((g) => !g.isIngredient)) {
    if (item.item && existingDrinkNames.has(item.item.toLowerCase())) {
      duplicates.push({ section: "drink", name: item.item });
    }
  }

  return { segmentsToCreate, duplicates };
}

// ─── Main import function ─────────────────────────────────────────────────────

/**
 * Imports a pure.html-format JSON export into Parse, appending to existing data.
 *
 * Segment mapping is controlled via options.segmentDecisions:
 *   - type "new" → creates a new segment
 *   - type "existing" → reuses the existing segment ID for drink linking;
 *     optionally updates its attendeeCount if updateCount is true
 *
 * Item dedup is controlled via options.itemsToOverwrite:
 *   - name in set → overwrites the existing Parse object
 *   - name not in set → skips (no duplicate created)
 */
export async function importLegacyFestivalData(
  data: LegacyFestivalData,
  store: FestivalCalcStore,
  options: ImportOptions
): Promise<ImportResult> {
  const result: ImportResult = {
    infraCount: 0,
    foodCount: 0,
    drinkCount: 0,
    recipeCount: 0,
    segmentsCreated: [],
    tagCreated: null,
    itemsSkipped: 0,
    itemsOverwritten: 0,
    warnings: [],
  };

  const currentUser = Parse.User.current();
  const zusagenPercent = toNum(data.zusagenPercent, 50);
  const aufbau = toNum(data.aufbau);
  const abbau = toNum(data.abbau);

  // ── 1. Ensure a "Standard" scenario tag exists ──────────────────────────────
  const existingStandardTag = store.config.tags.find(
    (t) => t.name.toLowerCase() === "standard"
  );

  let standardTagId: string;
  if (existingStandardTag) {
    standardTagId = existingStandardTag.id;
  } else {
    standardTagId = generateId();
    const newTag: ScenarioTag = {
      id: standardTagId,
      name: "Standard",
      color: "#1890ff",
      order: store.config.tags.length,
    };
    store.config.tags = [...store.config.tags, newTag];
    result.tagCreated = "Standard";
  }

  // ── 2. Resolve segments from decisions ──────────────────────────────────────
  const analysis = analyzeImport(data, store);
  const importedCountByName = new Map(
    analysis.segmentsToCreate.map((s) => [s.name, s.attendeeCount])
  );

  const newSegments: EventSegment[] = [];
  const drinkSegmentIds: string[] = [];
  let updatedSegments = [...store.config.eventSegments];

  for (const decision of options.segmentDecisions) {
    if (decision.type === "new") {
      const seg: EventSegment = {
        id: generateId(),
        name: decision.importedName,
        attendeeCount: importedCountByName.get(decision.importedName) ?? 0,
      };
      newSegments.push(seg);
      drinkSegmentIds.push(seg.id);
      result.segmentsCreated.push(seg.name);
    } else if (decision.type === "existing" && decision.existingId) {
      drinkSegmentIds.push(decision.existingId);
      if (decision.updateCount) {
        const newCount = importedCountByName.get(decision.importedName) ?? 0;
        updatedSegments = updatedSegments.map((s) =>
          s.id === decision.existingId ? { ...s, attendeeCount: newCount } : s
        );
      }
    }
  }

  // ── 3. Update config ─────────────────────────────────────────────────────────
  store.config = {
    ...store.config,
    zusagenPercent,
    aufbau: store.config.aufbau || aufbau,
    abbau: store.config.abbau || abbau,
    eventSegments: [...updatedSegments, ...newSegments],
    activeTagId: store.config.activeTagId ?? standardTagId,
  };
  await store.saveConfig();

  // ── 4. Import Infrastruktur items ────────────────────────────────────────────
  const infraItems = data.infra ?? [];
  let infraOrder = store.infraItems.length;
  for (const legacy of infraItems) {
    const name = legacy.item ?? "";
    const existing = store.infraItems.find(
      (i) => i.name.toLowerCase() === name.toLowerCase()
    );

    if (existing) {
      if (!options.itemsToOverwrite.has(name.toLowerCase())) {
        result.itemsSkipped++;
        continue;
      }
      const obj = new Parse.Object("SCH_InfraItem");
      obj.id = existing.objectId;
      obj.set("name", name);
      obj.set("itemType", legacy.typ ?? "");
      obj.set("active", legacy.active !== false);
      obj.set("tagPrices", [{ tagId: standardTagId, amount: toNum(legacy.amount) }]);
      await obj.save();
      result.infraCount++;
      result.itemsOverwritten++;
    } else {
      const obj = new Parse.Object("SCH_InfraItem");
      obj.set("name", name);
      obj.set("itemType", legacy.typ ?? "");
      obj.set("active", legacy.active !== false);
      obj.set("tagPrices", [{ tagId: standardTagId, amount: toNum(legacy.amount) }]);
      obj.set("order", infraOrder++);
      if (currentUser) obj.set("createdBy", currentUser);
      await obj.save();
      result.infraCount++;
    }
  }

  // ── 5. Import Essen items ────────────────────────────────────────────────────
  const essenItems = data.essen ?? [];
  let foodOrder = store.foodItems.length;
  for (const legacy of essenItems) {
    const name = legacy.item ?? "";
    const existing = store.foodItems.find(
      (i) => i.name.toLowerCase() === name.toLowerCase()
    );

    if (existing) {
      if (!options.itemsToOverwrite.has(name.toLowerCase())) {
        result.itemsSkipped++;
        continue;
      }
      const obj = new Parse.Object("SCH_FoodItem");
      obj.id = existing.objectId;
      obj.set("name", name);
      obj.set("active", legacy.active !== false);
      obj.set("tagPrices", [{ tagId: standardTagId, pricePerPerson: toNum(legacy.price) }]);
      obj.set("persons", toInt(legacy.persons));
      obj.set("eventSegmentIds", []);
      await obj.save();
      result.foodCount++;
      result.itemsOverwritten++;
    } else {
      const obj = new Parse.Object("SCH_FoodItem");
      obj.set("name", name);
      obj.set("active", legacy.active !== false);
      obj.set("tagPrices", [{ tagId: standardTagId, pricePerPerson: toNum(legacy.price) }]);
      obj.set("persons", toInt(legacy.persons));
      obj.set("eventSegmentIds", []);
      obj.set("order", foodOrder++);
      if (currentUser) obj.set("createdBy", currentUser);
      await obj.save();
      result.foodCount++;
    }
  }

  // ── 6. Import Getränke items (with optional recipes) ─────────────────────────
  const getraenkeItems = (data.getraenke ?? []).filter((g) => !g.isIngredient);
  let drinkOrder = store.drinkItems.length;
  for (const legacy of getraenkeItems) {
    const name = legacy.item ?? "";
    const existing = store.drinkItems.find(
      (i) => i.name.toLowerCase() === name.toLowerCase()
    );

    if (existing && !options.itemsToOverwrite.has(name.toLowerCase())) {
      result.itemsSkipped++;
      continue;
    }

    let recipePtr: Parse.Object | null = null;
    const zutaten = legacy.zutaten ?? [];
    const activeZutaten = zutaten.filter((z) => z.active !== false);

    if (activeZutaten.length > 0) {
      const recipeObj = new Parse.Object("SCH_DrinkRecipe");
      recipeObj.set("name", name || "Importiertes Rezept");
      recipeObj.set("description", "Importiert aus alter Kalkulation");
      if (currentUser) recipeObj.set("createdBy", currentUser);
      const savedRecipe = await recipeObj.save();
      recipePtr = savedRecipe;
      result.recipeCount++;

      for (let j = 0; j < zutaten.length; j++) {
        const zutat = zutaten[j];
        const ingr = new Parse.Object("SCH_Ingredient");
        ingr.set("recipe", savedRecipe);
        ingr.set("active", zutat.active !== false);
        ingr.set("name", zutat.item ?? "");
        ingr.set("quantity", toNum(zutat.einheiten, 1));
        ingr.set("unit", zutat.einheit ?? "");
        ingr.set("packageSize", toNum(zutat.gebindeGroesse, 1) || 1);
        ingr.set("packagePrice", toNum(zutat.gebindePreis ?? zutat.preis, 0));
        ingr.set("order", j);
        if (currentUser) ingr.set("createdBy", currentUser);
        await ingr.save();
      }
    } else if (zutaten.length > 0) {
      result.warnings.push(
        `Getränk "${name}": ${zutaten.length} Zutat(en) vorhanden, aber keine aktiv – Rezept wurde nicht importiert.`
      );
    }

    if (existing) {
      const obj = new Parse.Object("SCH_DrinkItem");
      obj.id = existing.objectId;
      obj.set("name", name);
      obj.set("drinkType", legacy.typ ?? "");
      obj.set("active", legacy.active !== false);
      obj.set("volumeMl", toInt(legacy.einheiten, 1) || 1);
      obj.set("servingsPerPerson", toInt(legacy.proKopf, 1) || 1);
      obj.set("tagPrices", [{ tagId: standardTagId, manualPrice: toNum(legacy.preis) }]);
      obj.set("eventSegmentIds", drinkSegmentIds);
      if (recipePtr) obj.set("recipe", recipePtr);
      await obj.save();
      result.drinkCount++;
      result.itemsOverwritten++;
    } else {
      const obj = new Parse.Object("SCH_DrinkItem");
      obj.set("name", name);
      obj.set("drinkType", legacy.typ ?? "");
      obj.set("active", legacy.active !== false);
      obj.set("volumeMl", toInt(legacy.einheiten, 1) || 1);
      obj.set("servingsPerPerson", toInt(legacy.proKopf, 1) || 1);
      obj.set("tagPrices", [{ tagId: standardTagId, manualPrice: toNum(legacy.preis) }]);
      obj.set("eventSegmentIds", drinkSegmentIds);
      if (recipePtr) obj.set("recipe", recipePtr);
      obj.set("order", drinkOrder++);
      if (currentUser) obj.set("createdBy", currentUser);
      await obj.save();
      result.drinkCount++;
    }
  }

  // ── 7. Reload store so the new items are visible ─────────────────────────────
  await store.loadAll();

  return result;
}

// ─── File reader helper ───────────────────────────────────────────────────────

export function parseLegacyJsonFile(file: File): Promise<LegacyFestivalData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const data = JSON.parse(text);
        if (
          typeof data !== "object" ||
          data === null ||
          (!Array.isArray(data.infra) && !Array.isArray(data.essen) && !Array.isArray(data.getraenke))
        ) {
          reject(new Error("Ungültiges Format: Datei enthält keine gültige Festival-Kalkulation."));
          return;
        }
        resolve(data as LegacyFestivalData);
      } catch {
        reject(new Error("JSON konnte nicht gelesen werden."));
      }
    };
    reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden."));
    reader.readAsText(file);
  });
}
